import { useState, useRef, useEffect } from "react";
import { X, Search, Upload, Loader2, CheckCircle, AlertCircle, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, CombinedVendor } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

type AnalysisStatus = 'idle' | 'analyzing' | 'success' | 'error';

interface VendorInfo {
  name: string;
  domain: string;
}

interface StartEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartEvaluation: (vendorId: string, vendorInfo?: VendorInfo) => void;
  existingVendorIds?: Set<string>;
  workspaceId?: string | null;
}

export default function StartEvaluationModal({ isOpen, onClose, onStartEvaluation, existingVendorIds = new Set(), workspaceId }: StartEvaluationModalProps) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const [vendorName, setVendorName] = useState("");
  const [website, setWebsite] = useState("");
  const [maxPages, setMaxPages] = useState(100);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Vendors from database
  const [vendors, setVendors] = useState<CombinedVendor[]>([]);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  
  // Analysis state
  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>('idle');
  const [analysisMessage, setAnalysisMessage] = useState("");
  const [analysisError, setAnalysisError] = useState("");
  const [maxPagesError, setMaxPagesError] = useState("");
  
  // Track if analysis is running (persists across modal close)
  const isAnalyzingRef = useRef(false);
  const currentAnalysisRef = useRef<VendorInfo | null>(null);
  const onStartEvaluationRef = useRef(onStartEvaluation);
  
  // Keep callback ref updated
  useEffect(() => {
    onStartEvaluationRef.current = onStartEvaluation;
  }, [onStartEvaluation]);

  // Load vendors from database when modal opens
  useEffect(() => {
    if (isOpen) {
      loadVendors();
    }
  }, [isOpen]);

  const loadVendors = async () => {
    setIsLoadingVendors(true);
    try {
      const vendorList = await api.listVendors('public');
      // Sort by most recently created
      const sorted = vendorList.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setVendors(sorted);
    } catch (err) {
      console.error('Failed to load vendors:', err);
      setVendors([]);
    } finally {
      setIsLoadingVendors(false);
    }
  };

  if (!isOpen) return null;

  const filteredVendors = vendors.filter(vendor =>
    !existingVendorIds.has(vendor.id) &&
    (vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (vendor.domain && vendor.domain.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  const showNoResults = searchQuery.length > 0 && filteredVendors.length === 0;

  const handleAddSelectedVendor = async () => {
    if (!selectedVendor) return;
    
    const vendor = vendors.find(v => v.id === selectedVendor);
    const vendorName = vendor?.name || selectedVendor;
    
    setAnalysisStatus('analyzing');
    setAnalysisMessage(`Adding ${vendorName} to workspace...`);
    
    try {
      // Notify parent to add vendor to workspace (does NOT start evaluation)
      onStartEvaluationRef.current(selectedVendor);
      
      setAnalysisStatus('success');
      setAnalysisMessage(`Successfully added ${vendorName} to workspace`);
      
      // Wait a moment then close
      setTimeout(() => {
        resetAndClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to add vendor:', err);
      setAnalysisStatus('error');
      setAnalysisError(
        err instanceof Error 
          ? err.message 
          : 'Failed to add vendor. Please try again.'
      );
    }
  };

  const handleVendorSelect = (vendorId: string) => {
    setSelectedVendor(vendorId);
  };

  const handleFileUpload = (file: File) => {
    setUploadedFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  // Extract domain from URL
  const extractDomain = (url: string): string => {
    try {
      let cleanUrl = url.trim();
      if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
        cleanUrl = 'https://' + cleanUrl;
      }
      const urlObj = new URL(cleanUrl);
      return urlObj.hostname.replace('www.', '');
    } catch {
      // If URL parsing fails, try to extract domain directly
      return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
    }
  };

  const handleAddNewVendor = async () => {
    if (!website.trim()) return;

    // Validate maxPages is between 1-100 (admin only)
    if (isAdmin && (maxPages < 1 || maxPages > 100)) {
      setMaxPagesError("Please enter a number between 1 and 100");
      return;
    }

    // Ensure URL has protocol
    let url = website.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    const domain = extractDomain(url);
    
    isAnalyzingRef.current = true;
    currentAnalysisRef.current = { name: vendorName || domain, domain };
    setAnalysisStatus('analyzing');
    setAnalysisMessage(`Adding ${domain} to library...`);
    setAnalysisError("");

    try {
      // Use async endpoint - returns immediately, crawl happens in background
      setAnalysisMessage(`Adding ${vendorName || domain} to your vendors...`);

      // Admins can set max_pages, otherwise use defaults (handled by backend)
      const result = await api.analyzeAsync(url, {
        name: vendorName || undefined,
        depth: 2,
        ...(isAdmin && { max_pages: maxPages }),
        ...(workspaceId && { workspace_id: workspaceId }),
      });

      console.log('Analyze async result:', result);

      if (!result.success) {
        throw new Error(result.message || result.error || 'Analysis failed');
      }

      if (!result.id) {
        console.error('No vendor ID returned from analysis');
        throw new Error('Analysis succeeded but no vendor ID was returned');
      }

      setAnalysisStatus('success');
      setAnalysisMessage(
        result.cached
          ? `Successfully added ${vendorName || domain} to your vendors.`
          : `${vendorName || domain} is being analyzed and should be ready in 2-3 minutes.`
      );

      // Notify parent that a vendor was added (this only adds to workspace, does NOT start evaluation)
      console.log('Vendor added with ID:', result.id);
      onStartEvaluationRef.current(result.id);

      // Wait a moment to show success message, then close
      setTimeout(() => {
        resetAndClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to add vendor:', err);
      setAnalysisStatus('error');
      setAnalysisError(
        err instanceof Error
          ? err.message
          : 'Failed to add vendor. Please check the URL and try again.'
      );
    } finally {
      isAnalyzingRef.current = false;
      currentAnalysisRef.current = null;
    }
  };

  const resetAndClose = () => {
    setSearchQuery("");
    setSelectedVendor(null);
    setVendorName("");
    setWebsite("");
    setMaxPages(10);
    setUploadedFile(null);
    setAnalysisStatus('idle');
    setAnalysisMessage("");
    setAnalysisError("");
    setMaxPagesError("");
    onClose();
  };

  const handleClose = () => {
    // Allow closing even during analysis - it will continue in the background
    // If analyzing, tell parent to show a pending card
    if (isAnalyzingRef.current && currentAnalysisRef.current) {
      console.log('Modal closed while analyzing - adding pending card');
      // Notify parent with vendor info so they can show a pending card
      onStartEvaluationRef.current('pending', currentAnalysisRef.current);
    }
    resetAndClose();
  };

  // Show analysis progress screen
  if (analysisStatus !== 'idle') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg w-full max-w-md p-8 relative">
          {/* Close button - always visible */}
          {analysisStatus === 'analyzing' && (
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2.5 hover:bg-muted rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              title="Close and continue in background"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          )}
          
          <div className="flex flex-col items-center text-center">
            {analysisStatus === 'analyzing' && (
              <>
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Adding Vendor
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {analysisMessage}
                </p>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-primary rounded-full animate-pulse" style={{ width: '60%' }} />
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  This may take a minute while we crawl the website...
                </p>
                <button
                  onClick={handleClose}
                  className="mt-4 text-sm text-primary hover:text-primary/80 font-medium"
                >
                  Close and continue in background →
                </button>
              </>
            )}

            {analysisStatus === 'success' && (
              <>
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Vendor Added
                </h3>
                <p className="text-sm text-muted-foreground">
                  {analysisMessage}
                </p>
              </>
            )}

            {analysisStatus === 'error' && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Analysis Failed
                </h3>
                <p className="text-sm text-red-500 mb-4">
                  {analysisError}
                </p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={resetAndClose}>
                    Cancel
                  </Button>
                  <Button variant="brand" onClick={() => setAnalysisStatus('idle')}>
                    Try Again
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Add New Vendor</h2>
            <p className="text-sm text-muted-foreground mt-1">Select or add an AI vendor to evaluate</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2.5 hover:bg-muted rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search for AI vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-primary/20 rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {showNoResults ? (
            /* New Vendor Form */
            <div className="space-y-4">
              {/* Information Message */}
              <div className="text-center py-2">
                <p className="text-sm text-muted-foreground">
                  Seems like this vendor is not yet in our knowledge base. Please provide the website URL and we'll evaluate the vendor for you.
                </p>
              </div>

              {/* Vendor Name Field */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Vendor Name <span className="text-muted-foreground">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  placeholder="Enter vendor name..."
                  className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Website Field */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Website <span className="text-destructive">*</span>
                </label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Pages to Crawl Field - Admin only */}
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Pages to Crawl <span className="text-muted-foreground">(1-100)</span>
                  </label>
                  <input
                    type="number"
                    value={maxPages}
                    onChange={(e) => {
                      setMaxPages(parseInt(e.target.value) || 0);
                      setMaxPagesError("");
                    }}
                    className={`w-full px-4 py-3 border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 ${
                      maxPagesError
                        ? 'border-destructive focus:border-destructive focus:ring-destructive'
                        : 'border-border focus:border-primary focus:ring-primary'
                    }`}
                  />
                  {maxPagesError ? (
                    <p className="text-xs text-destructive mt-1">
                      {maxPagesError}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                      Number of pages to analyze from the vendor's website. More pages = more comprehensive analysis.
                    </p>
                  )}
                </div>
              )}

              {/* File Upload
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Upload Sales Material <span className="text-muted-foreground">(Optional)</span>
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    isDragOver
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <Upload className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                  {uploadedFile ? (
                    <div>
                      <p className="text-sm font-medium text-foreground">{uploadedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <button
                        onClick={() => setUploadedFile(null)}
                        className="text-xs text-destructive hover:text-destructive/80 mt-1"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">
                        Drag and drop files here, or{" "}
                        <label className="text-primary cursor-pointer hover:text-primary/80">
                          browse
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileUpload(file);
                            }}
                            accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                          />
                        </label>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        PDF, DOC, TXT, or image files up to 10MB
                      </p>
                    </div>
                  )}
                </div>
              </div> */}
            </div>
          ) : (
            /* Vendors from Database */
            <div className="mb-6">
              <h3 className="text-lg font-medium text-foreground mb-4">
                {vendors.length > 0 ? 'Available Vendors' : 'Your Vendors'}
              </h3>
              
              {isLoadingVendors ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-primary mr-2" />
                  <span className="text-sm text-muted-foreground">Loading vendors...</span>
                </div>
              ) : filteredVendors.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredVendors.map((vendor) => (
                    <button
                      key={vendor.id}
                      onClick={() => handleVendorSelect(vendor.id)}
                      className={`flex items-center gap-4 p-4 rounded-lg border-2 text-left transition-all hover:shadow-sm ${
                        selectedVendor === vendor.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                        <Globe className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground truncate">{vendor.name}</h4>
                        <p className="text-sm text-muted-foreground truncate">{vendor.domain}</p>
                      </div>
                      {selectedVendor === vendor.id && (
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ) : vendors.length === 0 ? (
                <div className="text-center py-8 px-4 rounded-lg border border-dashed border-border">
                  <Globe className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-1">No vendors in your library yet</p>
                  <p className="text-xs text-muted-foreground">
                    Add a new vendor by entering their website URL above
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/20">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {showNoResults ? (
            <Button 
              variant="brand" 
              onClick={handleAddNewVendor}
              disabled={!website.trim()}
            >
              Add Vendor
            </Button>
          ) : (
            <Button 
              variant="brand" 
              onClick={handleAddSelectedVendor}
              disabled={!selectedVendor}
            >
              Add Vendor
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
